import { type QueryClient, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { clearUserLocalState } from '../app/local-state';
import { clearOfflineData } from '../app/pwa';
import { api, call } from './client';
import { ApiError } from './errors';
import { qk } from './keys';
import type { Me } from './types';

const HINT_KEY = 'ers.session';
const LOGOUT_PENDING_KEY = 'ers.logout-pending';

/**
 * Позначка «у цьому браузері є сеанс». Cookie сеансу httpOnly, тож без позначки інтерфейс не знає,
 * чи варто питати /me: зайвий запит без сеансу дає 401 у консолі браузера. Сервер лишається джерелом істини.
 */
export function hasSessionHint(): boolean {
  try {
    return window.localStorage.getItem(HINT_KEY) === '1';
  } catch {
    return true;
  }
}

export function setSessionHint(on: boolean) {
  try {
    if (on) {
      window.localStorage.setItem(HINT_KEY, '1');
      // Новий вхід скасовує відкладений вихід попереднього сеансу: інакше він завершив би щойно розпочатий.
      window.localStorage.removeItem(LOGOUT_PENDING_KEY);
    } else window.localStorage.removeItem(HINT_KEY);
  } catch {
    /* сховище недоступне */
  }
}

function setLogoutPending(on: boolean) {
  try {
    if (on) window.localStorage.setItem(LOGOUT_PENDING_KEY, '1');
    else window.localStorage.removeItem(LOGOUT_PENDING_KEY);
  } catch {
    /* сховище недоступне */
  }
}

/**
 * Вихід без мережі не дійшов до сервера: cookie сеансу ще дійсна. Повторюємо запит, щойно з'явиться зв'язок
 * (запуск застосунку, подія online), — доки хтось не ввійшов знову.
 */
export async function retryPendingLogout(): Promise<void> {
  let pending = false;
  try {
    pending = window.localStorage.getItem(LOGOUT_PENDING_KEY) === '1';
  } catch {
    return;
  }
  if (!pending) return;
  try {
    await call(api.POST('/api/v1/auth/logout'));
    setLogoutPending(false);
  } catch (e) {
    // Сервер відповів (будь-яким кодом) — повторювати нема чого; не відповів — спробуємо наступного разу.
    if (e instanceof ApiError && e.status !== 0) setLogoutPending(false);
  }
}

/** Поточний користувач, організація й області доступу. null — не ввійшов. */
export function useMeQuery(enabled = true) {
  return useQuery<Me | null>({
    queryKey: qk.me,
    queryFn: async () => {
      try {
        const me = await call(api.GET('/api/v1/me'));
        setSessionHint(true);
        return me;
      } catch (e) {
        if (e instanceof ApiError && e.status === 401) {
          setSessionHint(false);
          return null;
        }
        throw e;
      }
    },
    enabled,
    staleTime: 5 * 60_000,
    retry: (count, e) => !(e instanceof ApiError && e.status < 500) && count < 2,
  });
}

export function useAuthConfig() {
  return useQuery({
    queryKey: qk.authConfig,
    queryFn: () => call(api.GET('/api/v1/auth/config')),
    staleTime: Infinity,
  });
}

export function useDevLogin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (email: string) => call(api.POST('/api/v1/auth/dev-login', { body: { email } })),
    onSuccess: async () => {
      setSessionHint(true);
      qc.clear();
      await qc.fetchQuery({ queryKey: qk.me, queryFn: () => call(api.GET('/api/v1/me')) });
    },
  });
}

/**
 * Вихід. Спершу з браузера прибираються офлайн-дані, черга відкладених дій і стан екранів — це не залежить
 * від мережі; потім сеанс анулюється на сервері. Без мережі запит лишається «відкладеним виходом».
 */
export function useLogout() {
  const qc = useQueryClient();
  return useMutation({
    // Не чекати мережі: інакше без зв'язку кнопка «Вийти» нічого не робила б.
    networkMode: 'always',
    mutationFn: async () => {
      await clearOfflineData();
      clearUserLocalState();
      try {
        await call(api.POST('/api/v1/auth/logout'));
        setLogoutPending(false);
      } catch (e) {
        if (e instanceof ApiError && e.status === 0) setLogoutPending(true);
        throw e;
      }
    },
    onSettled: () => endSessionInPage(qc),
  });
}

/**
 * Сеанс у цій вкладці завершено: «я» стає null (оболонка переходить на сторінку входу), а дані попереднього
 * працівника зникають із пам'яті. Запити скасовуються до очищення — інакше відкриті екрани одразу перезапитали б
 * усе під уже недійсним сеансом.
 */
export function endSessionInPage(qc: QueryClient) {
  setSessionHint(false);
  qc.setQueryData(qk.me, null);
  void qc.cancelQueries();
  window.setTimeout(() => {
    qc.removeQueries({ predicate: (q) => q.queryKey[0] !== qk.me[0] });
    qc.getMutationCache().clear();
  }, 0);
}

export function googleStartUrl(redirect: string) {
  return `/api/v1/auth/google/start?redirect=${encodeURIComponent(redirect)}`;
}

/** Повідомлення для кодів помилок повернення з Google (/login?error=…). */
export const LOGIN_ERRORS: Record<string, string> = {
  google_denied: 'Вхід через Google скасовано або не дозволено.',
  state_expired: 'Час на вхід минув. Спробуйте ще раз.',
  token_exchange: 'Google не підтвердив вхід. Спробуйте ще раз.',
  no_id_token: 'Google не передав дані облікового запису. Спробуйте ще раз.',
  id_token_invalid: 'Не вдалося перевірити підпис Google. Спробуйте ще раз.',
  nonce: 'Перевірка входу не пройшла. Спробуйте ще раз.',
  email_unverified: 'Адресу електронної пошти в Google не підтверджено.',
  domain: 'Увійти можна лише з робочого облікового запису фірми.',
  org_missing: 'Організацію для цього домену не налаштовано. Зверніться до адміністратора.',
  suspended: 'Обліковий запис деактивовано. Зверніться до адміністратора.',
};
